import React from 'react';
import { Col, Row, theme as antdTheme } from 'antd';

export type KpiTone = 'default' | 'good' | 'accent';

export type KpiCardDef = {
  key: string;
  label: string;
  value: React.ReactNode;
  sub?: string;
  tone?: KpiTone;
  onClick?: () => void;
};

type ToneStyle = { bg?: string; labelColor?: string; valueColor?: string };

const TONE_STYLES: Record<KpiTone, ToneStyle> = {
  default: {},
  good: { bg: '#EEF7EF', labelColor: '#4B8A52', valueColor: '#16A34A' },
  accent: { bg: '#FEF0E6', labelColor: '#9A3C1A', valueColor: '#EA580C' },
};

type Props = {
  cards: KpiCardDef[];
};

// Presentational KPI-tile row, shared by the Overview and Lifecycle tabs.
const KpiCards = ({ cards }: Props) => {
  const { useToken } = antdTheme;
  const { token } = useToken();

  return (
    <Row gutter={[12, 12]}>
      {cards.map((card) => {
        const tone = TONE_STYLES[card.tone ?? 'default'];

        return (
          <Col xs={24} sm={12} lg={6} key={card.key}>
            <div
              style={{
                border: `1px solid ${tone.bg ? 'transparent' : token.colorBorderSecondary}`,
                borderRadius: 8,
                padding: '16px 20px',
                height: '100%',
                background: tone.bg ?? token.colorBgContainer,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: tone.labelColor ?? token.colorTextTertiary,
                  marginBottom: 4,
                }}
              >
                {card.label}
              </div>
              <div
                style={{
                  fontSize: 36,
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: tone.valueColor ?? token.colorText,
                  cursor: card.onClick ? 'pointer' : 'default',
                }}
                onClick={card.onClick}
                role={card.onClick ? 'button' : undefined}
                tabIndex={card.onClick ? 0 : undefined}
                onKeyDown={
                  card.onClick
                    ? (evt) => evt.key === 'Enter' && card.onClick?.()
                    : undefined
                }
              >
                {card.value}
              </div>
              {card.sub && (
                <div
                  style={{
                    fontSize: 12.5,
                    color: tone.labelColor ?? token.colorTextSecondary,
                    marginTop: 2,
                  }}
                >
                  {card.sub}
                </div>
              )}
            </div>
          </Col>
        );
      })}
    </Row>
  );
};

export default KpiCards;
